import axiosInstance from './apiClient'
import { getErrorMessage } from '@/lib/utils'
import type { MCPServer } from '@/api/mcpServer'
import type { LLMChoice } from '@/redux/types/room'

export type DummyBotReprocessDocumentResultItem =
  | {
      document_id: number
      document_name: string
      status: 'success'
      chunks_created: number
    }
  | {
      document_id: number
      document_name: string
      status: 'skipped'
      reason: string
    }
  | {
      document_id: number
      document_name: string
      status: 'failed'
      error: string
    }

export type DummyBotReprocessDocumentsResponse = {
  success: boolean
  processed: number
  skipped: number
  failed: number
  results: DummyBotReprocessDocumentResultItem[]
  message?: string
  error?: string
}

export type DummyBot = {
  id: number
  room: string
  name: string
  persona_instructions: string
  chatbot_llm: LLMChoice
  temperature: number
  max_tokens: number
  history_limit: number
  rag_enabled: boolean
  rag_top_k: number
  rag_chunk_size: number
  rag_chunk_overlap: number
  rag_similarity_threshold: number
  rag_max_context_length: number
  files?: Array<any>
  file_ids?: number[]
  document_settings?: Array<any>
  document_settings_data?: Record<string, any>
  mcp_enabled?: boolean
  mcp_servers?: MCPServer[]
  mcp_server_ids?: number[]
  is_active: boolean
  lock_during_active_round: boolean
  version: number
  created_at: string
  updated_at: string
}

export type DummyBotAssignment = {
  id: number
  bot: number
  bot_name: string
  team: number
  team_name: string
  room: number
  is_active: boolean
  assigned_at: string
  unassigned_at?: string | null
}

export const fetchDummyBots = async (roomId: string): Promise<DummyBot[]> => {
  try {
    const response = await axiosInstance.get(`/oge/chatbot/rooms/${roomId}/dummy-bots/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const createDummyBot = async (
  roomId: string,
  payload: Partial<DummyBot>
): Promise<DummyBot> => {
  try {
    const response = await axiosInstance.post(
      `/oge/chatbot/rooms/${roomId}/dummy-bots/`,
      payload
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const updateDummyBot = async (
  botId: number,
  payload: Partial<DummyBot>
): Promise<DummyBot> => {
  try {
    const response = await axiosInstance.patch(
      `/oge/chatbot/dummy-bots/${botId}/`,
      payload
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const deleteDummyBot = async (botId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/oge/chatbot/dummy-bots/${botId}/`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const assignDummyBotToTeam = async (
  botId: number,
  teamId: number
): Promise<DummyBotAssignment> => {
  try {
    const response = await axiosInstance.post(
      `/oge/chatbot/dummy-bots/${botId}/assign-team/`,
      { team_id: teamId }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const unassignDummyBot = async (botId: number): Promise<{ updated: number }> => {
  try {
    const response = await axiosInstance.post(
      `/oge/chatbot/dummy-bots/${botId}/unassign-team/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const fetchDummyBotAssignment = async (
  botId: number
): Promise<DummyBotAssignment | null> => {
  try {
    const response = await axiosInstance.get(
      `/oge/chatbot/dummy-bots/${botId}/assignment/`
    )
    if (response.data?.assignment === null) {
      return null
    }
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const fetchTeamDummyAssignment = async (
  roomId: string,
  teamId: number
): Promise<DummyBotAssignment | null> => {
  try {
    const response = await axiosInstance.get(
      `/oge/chatbot/rooms/${roomId}/dummy-bot-assignment/`,
      { params: { team_id: teamId } }
    )
    return response.data?.assignment || null
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const reprocessDummyBotDocuments = async (
  botId: number,
  documentIds?: number[]
): Promise<DummyBotReprocessDocumentsResponse> => {
  try {
    const payload: { document_ids?: number[] } = {}
    if (documentIds !== undefined) payload.document_ids = documentIds
    const response = await axiosInstance.post<DummyBotReprocessDocumentsResponse>(
      `/oge/chatbot/dummy-bots/${botId}/reprocess-documents/`,
      payload
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const fetchDummyBotMCPServers = async (
  botId: number
): Promise<MCPServer[]> => {
  try {
    const response = await axiosInstance.get<MCPServer[]>(
      `/oge/chatbot/dummy-bots/${botId}/mcp-servers/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
